import React, { useState } from 'react';

import { Col, Row, Button, Label, Input, Loading } from 'atoms';
import { useTranslation } from 'react-i18next';
import { i18n } from 'lib/i18';
import { PaymentProcessor, useGetDpayEntityInvestorQuery } from 'services/apollo';
import * as math from 'mathjs';

interface ExternalWalletManagementProps {
  externalBalance?: number;
}
// this is so typescript knows that we have installed the kladot library and it has added the AddFunds function to window
declare global {
  interface Window {
    AddFunds: (amount: string, token: string, username: string) => unknown;
  }
}

const ExternalWalletManagement: React.FC<ExternalWalletManagementProps> = ({ externalBalance }) => {
  const { t } = useTranslation();
  const [topUpAmount, setTopUpAmount] = useState(0);
  const { data: dpayEntityData, loading: dpayEntityLoading } = useGetDpayEntityInvestorQuery();
  if (dpayEntityLoading || !dpayEntityData) {
    return <Loading />;
  }

  if (!dpayEntityData?.getDpayEntityInvestor) {
    return <></>;
  }
  const dpayEntity = dpayEntityData.getDpayEntityInvestor;

  // specific implementations for top up validation of each payment processor/provider
  const validateTopUpAmountActions: {
    [key: string]: (amount: number) => number;
  } = {
    [PaymentProcessor.Kladot]: (amount: number) => {
      // ensure no more than 2 decimal places or kladot breaks. use math library for precision
      const amountTwoDecimals = math.floor(math.multiply(amount, 100)) / 100;
      // ensure amount is at least 0.5 or kladot breaks
      const validatedAmount = Math.max(amountTwoDecimals, 0.5);
      return validatedAmount;
    },
    // just as a placeholder
    [PaymentProcessor.Dwolla]: () => {
      return 0;
    },
  };

  // specific implementations for top up procedure of each payment processor/provider
  const balanceTopUpActions: {
    [key: string]: () => unknown;
  } = {
    [PaymentProcessor.Kladot]: () => {
      // ensure no more than 2 decimal places or kladot breaks
      const { token, paymentAccountID } = dpayEntity;
      window.AddFunds(topUpAmount.toString(), token, paymentAccountID);
    },
    // just as a placeholder
    [PaymentProcessor.Dwolla]: () => {
      return 0;
    },
  };

  const isTopUpAmountValid = (): boolean => {
    return validateTopUpAmountActions[dpayEntity.paymentProcessor](topUpAmount) === topUpAmount;
  };

  const externalBalanceTopUp = () => {
    if (isTopUpAmountValid()) {
      balanceTopUpActions[dpayEntity.paymentProcessor]();
    }
  };

  const handleTopUpAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.currentTarget.value);
    // validate the entered amount according to the payment processor's requirements
    const validatedAmount = validateTopUpAmountActions[dpayEntity.paymentProcessor](value);
    setTopUpAmount(validatedAmount);
  };

  return (
    <Row className="mb-3">
      <Col md={3}>
        <h4 className="mt-0 pb-2"> {t('WalletManagement-ExternalBalance')} </h4>
        <p>
          {(externalBalance || 0).toLocaleString(i18n.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </p>
      </Col>
      <Col md={3}>
        <Row align-items-center>
          <Label>{t('WalletManagement-TopUpAmountLabel')}</Label>
        </Row>
        <Row>
          <Col md={4}>
            <Input value={topUpAmount} type="number" onChange={handleTopUpAmountChange} />
          </Col>
          <Col md={6}>
            <Button onClick={externalBalanceTopUp} height>
              {' '}
              {t('WalletManagement-TopUpAmountButton')}{' '}
            </Button>
          </Col>
        </Row>
      </Col>
      <Col md={2}>
        <Row />
      </Col>
    </Row>
  );
};

export default ExternalWalletManagement;
